/* eslint-disable max-lines-per-function, max-statements, func-style */
import {
  DAS_ADD_ACCOMMODATION_TO_BASKET_FAILED,
  DAS_ADD_EXTRAS_TO_BASKET_FAILED,
} from '../../constants/errors';
import {
  addAccommodationExtraToBasket,
  addAccommodationToBasket,
  createBasket,
} from '../../api/basket';
import { basketOperations, basketTypes } from '../../store/basket';
import { call, put, takeLatest } from 'redux-saga/effects';

import { createError } from '../../store/errors';
import groupBy from 'lodash/groupBy';
import { messageOperations } from '../../store/messages';
import { reloadBasket } from './helpers';

const { ADD_ACCOMMODATION_AND_EXTRAS_TO_BASKET } = basketTypes;
const { sendMessage } = messageOperations;

// Build accommodation extras payload for API call
export const buildPayload = (accoId, index, selectedExtras) => {
  const groupedExtras = groupBy(selectedExtras, 'roomId');
  const roomId = index + 1;

  if (!groupedExtras[roomId]) {
    return [];
  }

  const extrasPayload = groupedExtras[roomId].map(
    ({ extra_id, group_id, id: option_id, name, other_information }) => ({
      acco_id: accoId.id,
      extra_id,
      pax_id: 1,
      group_id,
      option_id,
      name,
      other_information,
    }),
  );

  return extrasPayload;
};

export function* performAddAccommodationAndExtrasTobasket(action) {
  const {
    addAccommodationToBasketSuccess,
    addAccommodationAndExtrasToBasketSuccess,
    addToBasketFailed,
    createBasketSuccess,
    resetBasket,
  } = basketOperations;

  const { bookingRef, selectedExtras, hasBasket, customerId } = action.payload;
  let { basketPax, selectedRooms } = action.payload;

  let addedAccommodations = [];

  try {
    if (!hasBasket) {
      const createBasketResponse = yield call(createBasket, bookingRef);

      // Handle web service errors.
      if (createBasketResponse.errors) {
        throw Error('Could not create basket');
      }

      yield put(
        createBasketSuccess(
          createBasketResponse.bookingRef,
          createBasketResponse.basket,
        ),
      );
      basketPax = createBasketResponse.basket.pax || [];
    }

    // find correct pax_id for logged in customer and set
    const pax_id =
      basketPax.find(({ customer_id }) => customer_id === customerId)?.id || 1;
    selectedRooms = selectedRooms.map(accom => ({ ...accom, pax_id }));

    const addToBasketResponse = yield call(
      addAccommodationToBasket,
      bookingRef,
      selectedRooms,
    );

    // set the added accommodations for use later in accommodation extras
    addedAccommodations = addToBasketResponse.accommodations;

    // Handle web service errors.
    if (addToBasketResponse.errors) {
      throw new Error('Could not create new selected accommodations');
    }

    const basketResponse = yield call(reloadBasket, bookingRef);
    if (basketResponse.errors) {
      throw new Error('Could not reload basket');
    }

    yield put(
      sendMessage({
        type: 'success',
        displayType: 'toast',
        message: `${selectedRooms.length} x ${selectedRooms[0].name} ${
          selectedRooms.length > 1 ? 'were' : 'was'
        } added to your basket.`,
      }),
    );

    yield put(
      addAccommodationToBasketSuccess(addToBasketResponse.accommodations),
    );
  } catch (error) {
    yield put(
      createError({
        code: DAS_ADD_ACCOMMODATION_TO_BASKET_FAILED,
      }),
    );
    yield put(addToBasketFailed());
    if (!hasBasket) {
      yield put(resetBasket(bookingRef));
    }
    return;
  }

  if (selectedExtras.length === 0) {
    return;
  }

  // add accommodation extras
  try {
    // Create accommodation API payload
    const extrasToAdd = addedAccommodations
      .map((added, index) => buildPayload(added, index, selectedExtras))
      .flatMap(el => el);

    const addToBasketResponse = yield call(
      addAccommodationExtraToBasket,
      bookingRef,
      extrasToAdd,
    );

    // Handle web service errors.
    if (addToBasketResponse.errors) {
      throw new Error('Error creating accommodation extras');
    }

    const basketResponse = yield call(reloadBasket, bookingRef);
    if (basketResponse.errors) {
      throw new Error('Could not retrieve basket');
    }

    if (!basketResponse.basket) {
      throw new Error('Missing basket in response');
    }

    const { accommodations, extras } = basketResponse.basket;

    // Loop over the accommodations and extras and append the data to the correct accomId
    // This is to make it easier to reference the correct extra for the accommodation
    basketResponse.basket.accommodations = accommodations.map(accom => ({
      ...accom,
      extras: extras.filter(extra => extra.id === accom.id) || [],
    }));

    // Push new state changes to basket
    yield put(
      addAccommodationAndExtrasToBasketSuccess(
        bookingRef,
        basketResponse.basket,
      ),
    );
  } catch (error) {
    yield put(
      createError({
        code: DAS_ADD_EXTRAS_TO_BASKET_FAILED,
      }),
    );
    yield put(addToBasketFailed());
  }
}

export default takeLatest(
  ADD_ACCOMMODATION_AND_EXTRAS_TO_BASKET,
  performAddAccommodationAndExtrasTobasket,
);
